/**
 * Pipeline orchestration hook.
 *
 * Manages per-stage state via useReducer. Each stage tracks its own
 * status independently. "Run All" sequences all 13 stages in order,
 * stopping on hard failures but continuing through partial results.
 */

import { useReducer, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateScanDependencies } from "./use-post-scan-refresh.js";
import {
  triggerDiscovery,
  triggerCanon,
  triggerClassification,
  triggerTemplates,
  triggerRenders,
  triggerScans,
  triggerNormalisations,
  triggerScreenshots,
  triggerScore,
  triggerReport,
  triggerStatement,
  triggerDocuments,
  triggerRecurring,
} from "../api/runs.js";

// ── Stage definitions ─────────────────────────────────────────────────────────

export type PipelineStage =
  | "discovery"
  | "canon"
  | "classification"
  | "templates"
  | "renders"
  | "scans"
  | "normalisations"
  | "screenshots"
  | "scores"
  | "reports"
  | "statements"
  | "documents"
  | "recurring";

export const PIPELINE_STAGES: PipelineStage[] = [
  "discovery",
  "canon",
  "classification",
  "templates",
  "renders",
  "scans",
  "normalisations",
  "screenshots",
  "scores",
  "reports",
  "statements",
  "documents",
  "recurring",
];

export const STAGE_LABELS: Record<PipelineStage, string> = {
  discovery: "Discovery",
  canon: "Canonicalisation",
  classification: "Classification",
  templates: "Template detection",
  renders: "Renders",
  scans: "Scans",
  normalisations: "Normalisations",
  screenshots: "Screenshots",
  scores: "Score",
  reports: "Report",
  statements: "Statement",
  documents: "Documents",
  recurring: "Recurring comparison",
};

// ── State ─────────────────────────────────────────────────────────────────────

export type StageStatus =
  | "idle"
  | "running"
  | "success"
  | "partial"
  | "failed"
  | "skipped";

export interface StageState {
  status: StageStatus;
  runId: string | null;
  warnings: string[];
  error: string | null;
}

export type PipelineState = Record<PipelineStage, StageState>;

function initialStageState(): StageState {
  return { status: "idle", runId: null, warnings: [], error: null };
}

function initialState(): PipelineState {
  return Object.fromEntries(
    PIPELINE_STAGES.map((s) => [s, initialStageState()]),
  ) as PipelineState;
}

// ── Reducer ───────────────────────────────────────────────────────────────────

type Action =
  | { type: "START"; stage: PipelineStage }
  | {
      type: "SUCCESS";
      stage: PipelineStage;
      runId: string;
      warnings: string[];
      status: "success" | "partial";
    }
  | { type: "FAILED"; stage: PipelineStage; error: string }
  | { type: "SKIPPED"; stage: PipelineStage }
  | { type: "RESET" };

function reducer(state: PipelineState, action: Action): PipelineState {
  switch (action.type) {
    case "START":
      return {
        ...state,
        [action.stage]: {
          status: "running",
          runId: null,
          warnings: [],
          error: null,
        },
      };
    case "SUCCESS":
      return {
        ...state,
        [action.stage]: {
          status: action.status,
          runId: action.runId,
          warnings: action.warnings,
          error: null,
        },
      };
    case "FAILED":
      return {
        ...state,
        [action.stage]: {
          status: "failed",
          runId: null,
          warnings: [],
          error: action.error,
        },
      };
    case "SKIPPED":
      return {
        ...state,
        [action.stage]: {
          status: "skipped",
          runId: null,
          warnings: [],
          error: null,
        },
      };
    case "RESET":
      return initialState();
    default:
      return state;
  }
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function usePipeline(websiteId: string, orgId?: string) {
  const [state, dispatch] = useReducer(reducer, undefined, initialState);
  const qc = useQueryClient();

  const invalidateAll = useCallback(() => {
    invalidateScanDependencies(qc, websiteId, orgId);
  }, [qc, websiteId, orgId]);

  // ── Individual stage runners ────────────────────────────────────────────────

  async function runStage<
    T extends {
      id: string;
      status: string;
      warnings?: string[];
      errorMessage?: string | null;
    },
  >(stage: PipelineStage, fn: () => Promise<T>): Promise<T | null> {
    dispatch({ type: "START", stage });
    try {
      const result = await fn();
      const warnings = result.warnings ?? [];
      const s =
        result.status === "failed"
          ? "failed"
          : result.status === "partial"
            ? "partial"
            : "success";
      if (s === "failed") {
        dispatch({
          type: "FAILED",
          stage,
          error: result.errorMessage ?? "Run failed",
        });
        return null;
      }
      dispatch({
        type: "SUCCESS",
        stage,
        runId: result.id,
        warnings,
        status: s as "success" | "partial",
      });
      return result;
    } catch (err) {
      dispatch({
        type: "FAILED",
        stage,
        error: err instanceof Error ? err.message : String(err),
      });
      return null;
    }
  }

  const runDiscovery = () =>
    runStage("discovery", () => triggerDiscovery(websiteId));
  const runCanon = () => runStage("canon", () => triggerCanon(websiteId));
  const runClassification = () =>
    runStage("classification", () => triggerClassification(websiteId));
  const runTemplates = () =>
    runStage("templates", () => triggerTemplates(websiteId));
  const runRenders = () => runStage("renders", () => triggerRenders(websiteId));
  const runScans = () => runStage("scans", () => triggerScans(websiteId));
  const runNormalisations = () =>
    runStage("normalisations", () => triggerNormalisations(websiteId));
  const runScreenshots = () =>
    runStage("screenshots", () => triggerScreenshots(websiteId));
  const runScores = () => runStage("scores", () => triggerScore(websiteId));
  const runReports = () => runStage("reports", () => triggerReport(websiteId));
  const runStatements = () =>
    runStage("statements", () => triggerStatement(websiteId));
  const runDocuments = () =>
    runStage("documents", () => triggerDocuments(websiteId));
  const runRecurring = () =>
    runStage("recurring", () => triggerRecurring(websiteId));

  const reset = () => dispatch({ type: "RESET" });

  // ── Run All ────────────────────────────────────────────────────────────────
  // Sequences all stages in order. A failed stage that is a hard dependency
  // causes the remainder of its dependency chain to be skipped.

  const runAll = useCallback(async () => {
    dispatch({ type: "RESET" });

    const discovery = await runStage("discovery", () =>
      triggerDiscovery(websiteId),
    );
    if (!discovery) {
      // All downstream stages depend on discovery
      for (const s of PIPELINE_STAGES.slice(1))
        dispatch({ type: "SKIPPED", stage: s });
      return;
    }

    const canon = await runStage("canon", () => triggerCanon(websiteId));
    if (!canon) {
      for (const s of PIPELINE_STAGES.slice(2))
        dispatch({ type: "SKIPPED", stage: s });
      return;
    }

    const classification = await runStage("classification", () =>
      triggerClassification(websiteId),
    );
    if (!classification) {
      for (const s of PIPELINE_STAGES.slice(3))
        dispatch({ type: "SKIPPED", stage: s });
      return;
    }

    await runStage("templates", () => triggerTemplates(websiteId));
    await runStage("renders", () => triggerRenders(websiteId));
    const scans = await runStage("scans", () => triggerScans(websiteId));
    if (!scans) {
      for (const s of [
        "normalisations",
        "screenshots",
        "scores",
        "reports",
        "statements",
      ] as PipelineStage[]) {
        dispatch({ type: "SKIPPED", stage: s });
      }
      // documents and recurring can still run
      await runStage("documents", () => triggerDocuments(websiteId));
      await runStage("recurring", () => triggerRecurring(websiteId));
      invalidateAll();
      return;
    }

    await runStage("normalisations", () => triggerNormalisations(websiteId));
    await runStage("screenshots", () => triggerScreenshots(websiteId));
    await runStage("scores", () => triggerScore(websiteId));
    await runStage("reports", () => triggerReport(websiteId));
    await runStage("statements", () => triggerStatement(websiteId));
    await runStage("documents", () => triggerDocuments(websiteId));
    await runStage("recurring", () => triggerRecurring(websiteId));

    invalidateAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteId, invalidateAll]);

  return {
    state,
    runAll,
    reset,
    runDiscovery,
    runCanon,
    runClassification,
    runTemplates,
    runRenders,
    runScans,
    runNormalisations,
    runScreenshots,
    runScores,
    runReports,
    runStatements,
    runDocuments,
    runRecurring,
    invalidateAll,
  };
}
